import { Component, EventEmitter, Inject, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonService } from "src/services/common.service";
import { InvestorService } from "src/services/investor.service";
import { Address, PincodeDetailsResponse } from "src/models/investor";

@Component({
  selector: "app-update-address-modal",
  templateUrl: "./update-address-modal.component.html",
  styleUrls: ["./update-address-modal.component.scss"],
})
export class UpdateAddressModalComponent implements OnInit {
  addressForm!: FormGroup;
  loading: boolean = false;
  pincodeDetailsLoading: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private investorService: InvestorService,
    public dialogRef: MatDialogRef<UpdateAddressModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { address: Address; user_id: number },
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.addressForm = this.formBuilder.group({
      user_id: [this.data.user_id],
      location: ["", [Validators.required]],
      landmark: [""],
      pin_code: ["", [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      geo_location_id: [""],
      district: [{ value: "", disabled: true }],
      city: [{ value: "", disabled: true }],
      state: [{ value: "", disabled: true }],
      country: [{ value: "", disabled: true }],
    });

    if (this.data && this.data.address) {
      this.addressForm.patchValue({
        user_id: this.data.user_id,
        location: this.data.address.location,
        landmark: this.data.address.landmark,
        pin_code: this.data.address.pin_code,
        geo_location_id: this.data.address.id,
        district: this.data.address.district,
        city: this.data.address.city,
        state: this.data.address.state,
        country: this.data.address.country,
      });
    }

    // Listen for pin code changes
    this.addressForm.get("pin_code")?.valueChanges.subscribe((value) => {
      if (value && value.toString().length === 6) {
        this.fetchPincodeDetails(value);
      } else {
        // Clear the location fields if pincode is invalid
        this.clearLocationFields();
      }
    });
  }

  fetchPincodeDetails(pinCode: string): void {
    this.pincodeDetailsLoading = true;
    this.investorService.fetchPincodeDetails(pinCode).subscribe({
      next: (response: PincodeDetailsResponse) => {
        if (response.data) {
          this.addressForm.patchValue({
            district: response.data.district,
            city: response.data.city,
            state: response.data.state,
            country: response.data.country,
            geo_location_id: response.data.id,
          });
        }
        this.pincodeDetailsLoading = false;
      },
      error: (err) => {
        this.commonService.openSnackbar("Failed to fetch pincode details");
        this.clearLocationFields();
        this.pincodeDetailsLoading = false;
      },
    });
  }

  clearLocationFields(): void {
    this.addressForm.patchValue({
      district: "",
      city: "",
      state: "",
      country: "",
      geo_location_id: "",
    });
  }

  submitForm(): void {
    this.loading = true;
    if (this.addressForm && this.addressForm.valid) {
      const formValue = this.addressForm.value;

      // Extract only the fields needed for the API
      const submitData = {
        user_id: formValue.user_id,
        location: formValue.location,
        landmark: formValue.landmark,
        geo_location_id: formValue.geo_location_id,
      };

      this.investorService.updateUserAddress(submitData).subscribe({
        next: (res) => {
          this.commonService.openSnackbar(res.message);
          this.dialogRef.close(true);
          this.loading = false;
        },
        error: (err) => {
          this.commonService.openSnackbar("Failed to update address");
          this.loading = false;
        },
      });
    } else {
      this.loading = false;
      this.commonService.openSnackbar(
        "Please fill all required fields correctly"
      );
    }
  }

  closeModal(): void {
    this.dialogRef.close(false);
  }
}
